import React from "react";
import Footer from "../footer/footer";
import "./software.scss";

// Example project data - you can update this with your actual projects
const projects = [
  {
    id: 1,
    title: "vad pro",
    client: "IYO",
    description: "A desktop audio application that interfaces with custom earmold headphones to deliver immersive 3D spatial audio experiences. The system utilizes advanced head tracking technology to create dynamic, responsive soundscapes. The application enables users to simulate and experience various acoustic environments in real-time, providing an innovative approach to spatial audio design and personalization.",
    media: "/vad-pro-demo.gif",
    link: "https://www.iyo.ai/vad-pro"
  },
  {
    id: 2,
    title: "FL Web",
    client: "Image Line",
    description: "Contributed to the development of FL Web, a browser-based version of FL Studio that brings professional music production capabilities to the web. The platform features seamless cross-compatibility, allowing users to export projects from the web application and seamlessly open them in the desktop version of FL Studio, maintaining full project fidelity and workflow continuity.",
    media: "/fl-web-demo.gif",
    link: "https://fl.studio/"
  },
  {
    id: 3,
    title: "KSHMR Reverb 1.2",
    client: "KSHMR",
    description: "An intelligent reverb plugin featuring dynamic volume and tone management that responds to and interacts with your music in real-time. The effect evolves organically with the sound, creating adaptive spatial textures that enhance and transform the audio signal. This responsive design allows the reverb to become an active participant in the mix, rather than a static effect.",
    media: "/kshmr-reverb-demo.gif",
    link: "https://www.waproduction.com/plugins/view/kshmr-reverb?srsltid=AfmBOoqwK7DF4Hffuge5NYp_DFsCKn8CskvbJ0SZWzH7JkrlXIuZObPm"
  },
  // {
  //   id: 4,
  //   title: "BlockBuilder",
  //   client: "McMaster University",
  //   description: "An innovative educational game platform inspired by Minecraft, designed to teach Python programming and 3D graphics concepts through interactive, hands-on learning. The platform combines the engaging, block-based building mechanics of Minecraft with structured programming challenges, allowing students to learn coding fundamentals while creating and manipulating 3D environments. This immersive approach bridges the gap between abstract programming concepts and tangible visual results, making complex topics in computer graphics and software development more accessible and engaging for learners.",
  //   media: "/blockbuilder-demo.gif",
  //   link: "https://www.mcmaster.ca/"
  // },
  {
    id: 4,
    title: "SYNTHIA",
    client: "SYNTHIA",
    description: "A DAW and desktop application that dynamically generates house music using AI. The engine produces MIDI and SF2 files that can be played back directly within the application. The platform features built-in automation for dynamic music generation, allowing users to regenerate individual stems and refine compositions. Completed tracks can be exported and rendered for further manipulation in other production environments.",
    media: "/synthia-demo.gif",
    link: ""
  },
];

class Software extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadedImages: {}
    };
    this.imageRefs = {};
  }

  handleImageLoad = (projectId) => {
    this.setState(prevState => ({
      loadedImages: {
        ...prevState.loadedImages,
        [projectId]: true
      }
    }));
  };

  handleImageError = (projectId) => {
    this.setState(prevState => ({
      loadedImages: {
        ...prevState.loadedImages,
        [projectId]: 'error'
      }
    }));
  };

  componentDidMount() {
    // Apple-style scroll animations with smooth reveal
    const observerOptions = {
      threshold: [0, 0.1, 0.3, 0.5, 0.7, 1],
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        const ratio = entry.intersectionRatio;
        
        if (ratio > 0.1) {
          entry.target.classList.add('visible');
          // Progressive reveal based on scroll position
          const opacity = Math.min(1, ratio * 2);
          const translateY = (1 - ratio) * 60;
          const scale = 0.96 + (ratio * 0.04);
          
          entry.target.style.opacity = opacity;
          entry.target.style.transform = `translateY(${translateY}px) scale(${scale})`;
        } else {
          entry.target.style.opacity = '0';
          entry.target.style.transform = 'translateY(60px) scale(0.96)';
        }
      });
    }, observerOptions);

    document.querySelectorAll('.software-project').forEach(el => {
      observer.observe(el);
    });

    // Smooth momentum scrolling
    let isScrolling = false;
    let scrollTimeout;

    const handleScroll = () => {
      if (!isScrolling) {
        isScrolling = true;
        document.body.classList.add('is-scrolling');
      }

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isScrolling = false;
        document.body.classList.remove('is-scrolling');
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  render() {
    return (
      <div className="software-page">
        <div className="software-header">
          <h1 className="software-title">dru</h1>
        </div>
        <div className="software-projects">
          {projects.map((project, index) => (
            <div key={project.id} className="software-project" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="project-media">
                {project.media ? (
                  <>
                    {!this.state.loadedImages[project.id] && (
                      <div className="project-loading">
                        <div className="loading-spinner"></div>
                      </div>
                    )}
                    <a 
                      href={project.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      <img 
                        ref={el => this.imageRefs[project.id] = el}
                        src={project.media} 
                        alt={project.title}
                        className={`project-gif ${project.id === 1 ? 'project-gif-small' : project.id === 2 ? 'project-gif-large' : 'project-gif-medium'} ${this.state.loadedImages[project.id] ? 'loaded' : ''}`}
                        onLoad={() => this.handleImageLoad(project.id)}
                        onError={() => this.handleImageError(project.id)}
                      />
                    </a>
                  </>
                ) : (
                  <div className="project-placeholder">
                    <span>Media</span>
                  </div>
                )}
              </div>
              <div className="project-details">
                <h2 className="project-title">{project.title}</h2>
                <div className="project-info">
                  <div className="info-row">
                    <span className="info-label">client:</span>
                    <span className="info-value">{project.client}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">description:</span>
                    <span className="info-value">{project.description}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Footer />
      </div>
    );
  }
}

export default Software;

